import React, { useEffect, useState } from 'react';
import { Notice } from './admin-feedback';
import { type AdminApiClient, type JsonRecord, isRecord } from './api-client';
import {
  BUSINESS_PERMISSION_GROUPS,
  FIELD_CHOICES,
  canonicalFieldRules,
  presentPermission,
  readScopeGroups,
  securityRecord,
  securityText,
  type BusinessSecurityWorkspace,
  type SecurityUser,
} from './business-security-model';

const records = (value: unknown): JsonRecord[] =>
  Array.isArray(value) ? value.filter(isRecord) : [];
const ids = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : [];

/** Reads the actual server decision; role drafts must never masquerade as effective access. */
export function UserPermissionCheck({
  api,
  user,
  data,
  dirty,
}: {
  api: AdminApiClient;
  user: SecurityUser;
  data: BusinessSecurityWorkspace;
  dirty: boolean;
}) {
  const [result, setResult] = useState<JsonRecord>();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    let live = true;
    setResult(undefined);
    setError('');
    setLoading(true);
    const timer = setTimeout(() => {
      controller.abort();
      if (live) {
        setError('权限检查超时，请重试。');
        setLoading(false);
      }
    }, 15_000);
    void api
      .request<unknown>(
        `/api/admin/m8/security/workspace/users/${encodeURIComponent(user.id)}/access`,
        { signal: controller.signal, cache: 'no-store' },
      )
      .then((raw) => {
        if (!live || controller.signal.aborted) return;
        const value = securityRecord(raw);
        if (
          !Array.isArray(value.permissions) ||
          (value.organizationContext !== null && !isRecord(value.organizationContext))
        )
          throw new Error('Incomplete permission check');
        setResult(value);
      })
      .catch(() => {
        if (live && !controller.signal.aborted) setError('最终权限尚未读取，请重试检查。');
      })
      .finally(() => {
        clearTimeout(timer);
        if (live && !controller.signal.aborted) setLoading(false);
      });
    return () => {
      live = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [api, user.id, user.version, revision]);
  const decisions = records(result?.permissions);
  const context = securityRecord(result?.organizationContext);
  const allowed = decisions.filter((decision) => decision.allowed === true);
  const centralOrdersAllowed = allowed.some(
    (decision) =>
      ['order.view', 'payment.view', 'refund.view', 'after_sales.view'].includes(
        securityText(decision, 'code'),
      ) &&
      records(decision.grants).some((grant) =>
        readScopeGroups(grant.businessScopeGroups).some(
          (scope) =>
            scope.includeCentralOrders &&
            scope.cityIds !== null &&
            scope.cityIds.length > 0 &&
            scope.productIds?.length !== 0 &&
            scope.orderIds?.length !== 0,
        ),
      ),
  );
  const name = (kind: 'apps' | 'cities' | 'products' | 'orders', id: string) => {
    const row = data[kind].find((item) => item.id === id);
    return row ? ('name' in row ? row.name : row.orderNo) : '已有授权资源（当前未列出）';
  };
  return (
    <details className="employee-editor-section user-permission-check">
      <summary>
        <strong>权限检查 · 当前已生效</strong>
        <span>
          {loading ? '正在检查…' : result ? `${allowed.length} 项功能获准` : '展开查看实际结果'}
        </span>
      </summary>
      <p className="employee-help">
        以下结果来自服务器。每项操作只可使用授予该操作的角色范围，并受组织归属与业务状态限制。
      </p>
      {dirty && (
        <Notice tone="warning">
          上方有未保存修改。此处仍显示最后保存的权限，保存后自动重新检查。
        </Notice>
      )}
      {loading && <Notice>正在检查这个用户的最终权限…</Notice>}
      {error && <Notice tone="warning">{error}</Notice>}
      <button type="button" disabled={loading} onClick={() => setRevision((value) => value + 1)}>
        重新检查权限
      </button>
      {result && (
        <>
          <p>
            账号状态：<strong>{user.status === 'ACTIVE' ? '启用' : '停用，无法登录操作'}</strong> ·{' '}
            {allowed.length} 项功能获准
          </p>
          <p>
            组织范围：
            {context.platformAccess === true
              ? '平台管理范围'
              : ids(context.allowedOrganizationIds)
                  .map((id) => data.organizations.find((org) => org.id === id)?.name ?? '已有组织')
                  .join('、') ||
                (centralOrdersAllowed
                  ? '未分配部门；仅可访问已明确授权范围内尚未分配部门的中央订单'
                  : '未分配可访问组织，部门订单不可访问')}
          </p>
          {BUSINESS_PERMISSION_GROUPS.map((group) => {
            const entries = decisions.filter((decision) => {
              const permission = data.permissions.find((item) => item.code === decision.code);
              return permission && presentPermission(permission).groupId === group.id;
            });
            if (!entries.length) return null;
            return (
              <details key={group.id} className="user-permission-group">
                <summary>
                  {group.name} · {entries.filter((entry) => entry.allowed === true).length}{' '}
                  项功能获准
                </summary>
                {entries.map((entry) => {
                  const permission = data.permissions.find((item) => item.code === entry.code)!;
                  return (
                    <div key={permission.id} className="user-permission-decision">
                      <strong>{presentPermission(permission).title}</strong>
                      <span>{entry.allowed === true ? '已授权' : '未授权或明确禁止'}</span>
                      {entry.allowed === true &&
                        records(entry.fieldPolicies).length > 0 &&
                        (() => {
                          const fields = canonicalFieldRules(
                            records(entry.fieldPolicies).map((rule) => ({
                              permissionId: permission.id,
                              fieldName: securityText(
                                rule,
                                'field',
                                securityText(rule, 'fieldName'),
                              ),
                              classification: securityText(rule, 'classification'),
                              access:
                                rule.effect === 'DENY' || rule.access === 'DENY'
                                  ? 'DENY'
                                  : rule.effect === 'MASK' || rule.access === 'MASK'
                                    ? 'MASK'
                                    : 'ALLOW',
                            })),
                          ).filter((rule) => rule.fieldName);
                          const readable = fields.filter((rule) => rule.access === 'ALLOW');
                          const limited = fields.filter((rule) => rule.access !== 'ALLOW');
                          return (
                            <p className="user-permission-fields">
                              常用字段：{readable.length} 项可读
                              {limited.length > 0 &&
                                `；显示限制：${limited
                                  .map(
                                    (rule) =>
                                      `${FIELD_CHOICES.find((field) => field.name === rule.fieldName)?.label ?? '原有业务信息'}${rule.access === 'MASK' ? '脱敏' : '隐藏'}`,
                                  )
                                  .join('、')}`}
                            </p>
                          );
                        })()}
                      {entry.allowed === true &&
                        records(entry.grants).every(
                          (grant) =>
                            grant.businessScopeGroups !== null &&
                            readScopeGroups(grant.businessScopeGroups).every(
                              (scope) =>
                                scope.cityIds?.length === 0 ||
                                (group.id === 'products' && scope.productIds?.length === 0) ||
                                (group.id === 'orders' &&
                                  (scope.productIds?.length === 0 || scope.orderIds?.length === 0)),
                            ),
                        ) && <p>功能已授权，暂无可管理数据。请为此操作配置管理范围。</p>}
                      {entry.allowed === true &&
                        records(entry.grants).map((grant, index) => (
                          <div key={`${securityText(grant, 'roleId')}:${index}`}>
                            <small>
                              来源：
                              {data.roles.find((role) => role.id === grant.roleId)?.name ??
                                '个人授权'}
                            </small>
                            {grant.businessScopeGroups === null ? (
                              <p>
                                {records(grant.dataScopes).some(
                                  (rule) => rule.scope === 'ALL' && rule.effect === 'ALLOW',
                                )
                                  ? '全部平台业务数据（仍受明确禁止规则限制）。'
                                  : '沿用已有授权范围（由服务器按原规则校验）。'}
                              </p>
                            ) : (
                              readScopeGroups(grant.businessScopeGroups).map(
                                (scope, scopeIndex) => (
                                  <p key={`${scope.appId}:${scopeIndex}`}>
                                    {name('apps', scope.appId)} · 城市：
                                    {scope.cityIds === null
                                      ? '全部已开放城市'
                                      : scope.cityIds.map((id) => name('cities', id)).join('、') ||
                                        '无'}
                                    {' · '}线路：
                                    {scope.productIds == null
                                      ? '所选城市内全部线路'
                                      : scope.productIds
                                          .map((id) => name('products', id))
                                          .join('、') || '无'}
                                    {' · '}订单：
                                    {scope.orderIds == null
                                      ? '上述范围内全部订单'
                                      : scope.orderIds.map((id) => name('orders', id)).join('、') ||
                                        '无'}
                                    {scope.includeCentralOrders && '（含尚未分配部门的新增订单）'}
                                  </p>
                                ),
                              )
                            )}
                            {records(entry.deniedScopes).length > 0 && (
                              <p>另有明确禁止的数据范围，以服务器实际访问判断为准。</p>
                            )}
                          </div>
                        ))}
                    </div>
                  );
                })}
              </details>
            );
          })}
          {!allowed.length && (
            <p className="employee-help">此用户目前没有可用功能。选择角色或直接授权后保存。</p>
          )}
        </>
      )}
    </details>
  );
}
